import { computed, watch } from 'vue'

import { LineupPlayer } from '@collector/desktop-feature-lineups'
import type { RabbitEventsLineupsMessage } from '@collector/queue-gateway-shared-types'
import { queryClient } from '@desktop/queryClient'

import { useSubParticipantsQuery } from './queries/useSubParticipantsQuery'
import { useQueueClient } from './queue/useQueueClient'
import { useEventId } from './useEventId'
import { useParticipants } from './useParticipants'

/**
 * Lineups socket → full SubParticipants refetch (authoritative replace).
 */
const lineupsHandler = async (
  eventId: number,
  messages: RabbitEventsLineupsMessage[],
) => {
  await Promise.all(
    messages.map(async (_) => {
      await queryClient.invalidateQueries({
        queryKey: ['event', 'subParticipants', eventId],
        exact: true,
      })
    }),
  )
}

export const useSubParticipants = () => {
  const eventId = useEventId()
  const { homeParticipant, awayParticipant } = useParticipants()
  const { data: subParticipants } = useSubParticipantsQuery()

  const homeSubParticipants = computed(() => {
    const homeParticipantId = homeParticipant.value?.id
    if (homeParticipantId === undefined) return []

    return (subParticipants.value ?? []).filter(
      (subParticipant) => Number(subParticipant.team_id) === homeParticipantId,
    )
  })

  const awaySubParticipants = computed(() => {
    const awayParticipantId = awayParticipant.value?.id
    if (awayParticipantId === undefined) return []

    return (subParticipants.value ?? []).filter(
      (subParticipant) => Number(subParticipant.team_id) === awayParticipantId,
    )
  })

  const setHomeSubParticipants = (newSubsParticipants: LineupPlayer[]) => {
    queryClient.setQueryData<LineupPlayer[]>(
      ['event', 'subParticipants', eventId.value],
      (current = []) => [
        ...newSubsParticipants,
        ...current.filter(
          (subParticipant) =>
            Number(subParticipant.team_id) === awayParticipant.value?.id,
        ),
      ],
    )
  }

  const setAwaySubParticipants = (newSubsParticipants: LineupPlayer[]) => {
    queryClient.setQueryData<LineupPlayer[]>(
      ['event', 'subParticipants', eventId.value],
      (current = []) => [
        ...current.filter(
          (subParticipant) =>
            Number(subParticipant.team_id) === homeParticipant.value?.id,
        ),
        ...newSubsParticipants,
      ],
    )
  }

  return {
    awaySubParticipants,
    homeSubParticipants,
    setHomeSubParticipants,
    setAwaySubParticipants,
  }
}

export const useLiveSubParticipants = () => {
  const { connected, client } = useQueueClient()
  const eventId = useEventId()

  watch(
    connected,
    (isConnected, _, onCleanup) => {
      if (!isConnected) return
      onCleanup(
        client.subscribe(`events/${eventId.value}/events_lineups`, {
          sync: { method: 'latest' },
          onMessageBatch: (messages) => lineupsHandler(eventId.value, messages),
        }),
      )
    },
    { immediate: true },
  )
}
